/**
 * Тонкий API-клиент. Cookie-сессия идёт через credentials: 'include'.
 */

async function http<T = unknown>(method: string, url: string, body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'include',
    // Only advertise a JSON body when we actually send one — Fastify 5 rejects
    // POSTs that carry content-type: application/json with an empty body
    // (FST_ERR_CTP_EMPTY_JSON_BODY), which broke bodyless calls like logout.
    headers: body !== undefined ? { 'content-type': 'application/json' } : {},
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const resp = await fetch(url, init);
  if (!resp.ok) {
    let detail = '';
    try {
      detail = await resp.text();
    } catch {
      /* ignore */
    }
    throw new HttpError(resp.status, detail);
  }
  const ct = resp.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await resp.json()) as T;
  return undefined as T;
}

export class HttpError extends Error {
  constructor(public status: number, public detail: string) {
    super(`HTTP ${status}: ${detail}`);
  }
}

/**
 * Authenticated binary download: fetch with the session cookie, then trigger a
 * browser save of the returned blob (same pattern as the recovery-codes .txt
 * download in Account.tsx, but for server-streamed octet-stream bodies).
 */
async function downloadBlob(url: string, filename: string): Promise<void> {
  const resp = await fetch(url, { credentials: 'include' });
  if (!resp.ok) {
    let detail = '';
    try {
      detail = await resp.text();
    } catch {
      /* ignore */
    }
    throw new HttpError(resp.status, detail);
  }
  const blob = await resp.blob();
  const obj = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = obj;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(obj);
}

/** Authenticated raw-bytes upload as application/octet-stream. */
async function uploadOctet<T = unknown>(url: string, bytes: ArrayBuffer): Promise<T> {
  const resp = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/octet-stream' },
    body: bytes,
  });
  if (!resp.ok) {
    let detail = '';
    try {
      detail = await resp.text();
    } catch {
      /* ignore */
    }
    throw new HttpError(resp.status, detail);
  }
  const ct = resp.headers.get('content-type') ?? '';
  if (ct.includes('application/json')) return (await resp.json()) as T;
  return undefined as T;
}

/** elabel fields extracted from an imported .hwrsv, loaded as pending edits. */
export interface HwrsvFields {
  bom?: string | null;
  level?: string | null;
  barcode?: string | null;
  model?: string | null;
  description?: string | null;
  manufactured?: string | null;
  boardType?: string | null;
  // Whitelist list type/version from a v2 .hwrsv (null if the file had none).
  whitelist?: { listType: string; listVersion: string } | null;
  // Raw DISK_AUTHENTICATION regions (r4/r5, base64) when the imported .hwrsv's
  // cert differs from the disk's current one; null = no auth change. Staged as a
  // pending "auth" change and flashed verbatim.
  authRegions?: { r4: string; r5: string } | null;
}

export type Role = 'superadmin' | 'admin' | 'client';

/** True for admin-tier roles (admin OR superadmin). Use this instead of a bare
 * `role === 'admin'` so superadmins retain every admin capability in the UI. */
export const isAdminRole = (r: Role | undefined | null): boolean =>
  r === 'admin' || r === 'superadmin';

/** One immutable quota-ledger row (spend / top-up / adjust). */
export interface QuotaLedgerRow {
  id: number;
  clientUserId: string;
  clientUsername: string | null;
  delta: number;
  balanceAfter: number;
  type: 'topup' | 'spend' | 'adjust' | 'expire';
  reason: string | null;
  deviceId: string | null;
  deviceLabel: string | null;
  sessionId: string | null;
  diskSerial: string | null;
  actorUserId: string | null;
  actorUsername: string | null;
  createdAt: number;
}

export interface StatsSummary {
  flashesToday: number;
  flashes7d: number;
  flashes30d: number;
  flashesTotal: number;
  // Distinct disks ever flashed (a disk re-flashed counts once).
  flashesUniqueDisks: number;
  quotaGrantedTotal: number;
  quotaConsumedTotal: number;
  quotaAvailableTotal: number;
  activeDevices: number;
  devicesOnline: number;
  activeClients: number;
}

export interface ChatMsg {
  id: number;
  threadClientId: string;
  senderUserId: string | null;
  senderRole: string;
  senderName: string | null;
  body: string;
  createdAt: number;
}
export interface ChatThread {
  clientId: string;
  clientName: string | null;
  lastBody: string;
  lastAt: number;
  lastSenderRole: string;
  unread: number;
}

export interface StorageStats {
  backupsCount: number;
  backupsBytes: number;
  dbBytes: number;
  diskFree: number;
  diskTotal: number;
  disksByType: { nvme: number; sas25: number; sas35: number; unknown: number; total: number };
}

export interface User {
  id: string;
  /** Primary login identity (server switched from email → username). */
  username: string;
  /** Optional secondary field kept where the user model still has it. */
  email?: string | null;
  role: Role;
  mustChangePassword?: boolean;
  /** Free-text admin note on the account (Users management). */
  note?: string | null;
}

export const api = {
  login: (username: string, password: string) =>
    http<{ user: User }>('POST', '/api/auth/login', { username, password }),
  logout: () => http<{ ok: true }>('POST', '/api/auth/logout'),
  changePassword: (currentPassword: string, newPassword: string) =>
    http<{ ok: true; user: User }>('POST', '/api/auth/password', { currentPassword, newPassword }),
  me: () => http<{ user: User; impersonating?: boolean; adminEmail?: string }>('GET', '/api/auth/me'),
  getServerInfo: () =>
    http<{
      role: string;
      version: string;
      commit: string;
      peerUrl: string | null;
      readOnly: boolean;
      feedbackEnabled: boolean;
    }>('GET', '/api/server/info'),
  // Disk certificate (r4/r5) conversion. Both STAGE new region bytes (base64) that
  // the editor flashes through the normal Apply path — they don't write the disk.
  cloneDiskCert: (sessionId: string, sn: string, caId?: string) =>
    http<{ r4: string; r5: string; status: 'our'; caId: string; caName: string; subject: string }>(
      'POST',
      `/api/admin/v1/sessions/${sessionId}/disks/${encodeURIComponent(sn)}/cert-clone`,
      caId ? { caId } : {},
    ),
  restoreDiskCert: (sessionId: string, sn: string) =>
    http<{ r4: string; r5: string; status: 'orig'; backupSource: string; backupAt: number }>(
      'POST',
      `/api/admin/v1/sessions/${sessionId}/disks/${encodeURIComponent(sn)}/cert-restore`,
    ),
  // Certificate authorities (superadmin). Built-in "Our" + generated clones.
  listCertAuthorities: () =>
    http<{
      authorities: Array<{
        id: string;
        name: string;
        builtin: boolean;
        canSign: boolean;
        isDefault: boolean;
        fingerprint: string;
        flashCount: number;
        createdAt: number | null;
      }>;
    }>('GET', '/api/admin/v1/cert-authorities'),
  createCertAuthority: (name: string, setDefault?: boolean) =>
    http<{ id: string; name: string }>('POST', '/api/admin/v1/cert-authorities', { name, setDefault }),
  updateCertAuthority: (id: string, patch: { name?: string; setDefault?: boolean }) =>
    http<{ ok: true }>('PATCH', `/api/admin/v1/cert-authorities/${id}`, patch),
  deleteCertAuthority: (id: string) =>
    http<{ ok: true; flashCount: number }>('DELETE', `/api/admin/v1/cert-authorities/${id}`),
  // Global system settings (superadmin).
  getSettings: () => http<{ feedbackEnabled: boolean }>('GET', '/api/admin/v1/settings'),
  updateSettings: (patch: { feedbackEnabled?: boolean }) =>
    http<{ feedbackEnabled: boolean }>('PUT', '/api/admin/v1/settings', patch),
  ssoStatus: () => http<{ enabled: boolean }>('GET', '/api/auth/sso/status'),
  impersonate: (userId: string, opts: { block?: boolean } = {}) =>
    http<{ ok: true; user: User }>('POST', `/api/auth/impersonate/${userId}`, opts),
  impersonationReturn: () =>
    http<{ ok: true; user: User }>('POST', '/api/auth/impersonation/return'),
  impersonationStatus: () =>
    http<{ blocked: boolean; adminName?: string | null; since?: number }>(
      'GET',
      '/api/client/v1/impersonation-status',
    ),

  listSessions: (opts: { status?: string; deviceId?: string; diskSn?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.status) qs.set('status', opts.status);
    if (opts.deviceId) qs.set('deviceId', opts.deviceId);
    if (opts.diskSn) qs.set('diskSn', opts.diskSn);
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return http<{
      total: number;
      items: Array<{
        id: string;
        deviceId: string;
        status: string;
        createdAt: number;
        updatedAt: number;
      }>;
    }>('GET', `/api/admin/v1/sessions${suffix}`);
  },

  getSession: (id: string) =>
    http<{
      id: string;
      deviceId: string;
      status: string;
      createdAt: number;
      updatedAt: number;
      diskCount: number;
      disks: Array<{
        serial: string;
        slot: number | null;
        // Physical frame/enclosure reported by the device; null when unknown.
        frame?: number | null;
        devPath: string | null;
        // `Valid` column from `sddebug showdisk` — controller's live disk status
        // ("Normal"/"Level"/…). null/absent → unknown (old session or /dev fallback).
        sddebugValid?: string | null;
        // Parsed current disk whitelist (NVMe only). null → the disk carries none,
        // so the editor hides the Whitelist field (never fabricates one). `version`
        // is the blob format version: reference only, never edited.
        whitelist?: {
          bom: string;
          version: string;
          listType: string;
          listVersion: string;
        } | null;
        // DISK_AUTHENTICATION (r4) cert trust: 'orig' (genuine Huawei CA), 'our'
        // (re-issued OEM CA), 'error' (other/invalid or CN != SN), null = no cert.
        // Which authority signed the disk cert (null = no parseable cert).
        certStatus?:
          | { match: 'orig' }
          | { match: 'ca'; caId: string; caName: string }
          | { match: 'error' }
          | null;
        summary: null | {
          serial: string;
          bom: string;
          level: string;
          barcode: string | null;
          barcodeDecoded: { material: string; date: string; rohs: string } | null;
          model: string | null;
          description: string | null;
          manufactured: string | null;
          vendorName: string | null;
          boardType: string | null;
          // Device/server-reported bus. Preferred over a devPath guess for the
          // Type column. Absent until the backend adds it → devPath fallback.
          transport?: string | null;
          // Physical form factor for SAS disks (2.5" vs 3.5", by block size).
          // NVMe → null. Absent until the backend adds it → Type falls back gracefully.
          formFactor?: '2.5' | '3.5' | null;
          integrity: {
            errorCount: number;
            warningCount: number;
            // Present since server ≥ the integrity-detail release; older stored
            // sessions omit it → the UI falls back to the plain counts.
            items?: Array<{ level: 'warning' | 'error'; category: string; message: string }>;
          };
        };
      }>;
      operatorNote: string | null;
      confirmResults: Array<{
        serial: string;
        status: string;
        detail?: string;
        powercycle?: string;
        returnedSec?: number;
      }> | null;
    }>('GET', `/api/admin/v1/sessions/${id}`),

  applyChanges: (
    sessionId: string,
    changesPerDisk: Record<
      string,
      {
        bom?: string;
        level?: string;
        elabelFields?: Record<string, string>;
      }
    >,
    powerCycleAfter?: boolean,
    // Per-disk whitelist list edits (keyed by serial). A disk may appear here
    // without any entry in changesPerDisk (a whitelist-only change).
    whitelistPerDisk?: Record<string, { listType?: string; listVersion?: string; bom?: string }>,
    // Per-disk raw DISK_AUTHENTICATION regions (r4/r5, base64) — a .hwrsv import
    // or a cert conversion (caId records which authority signed it).
    authRegionsPerDisk?: Record<string, { r4: string; r5: string; caId?: string }>,
  ) =>
    http<{
      sessionId: string;
      approvalId: string;
      diskOutcomes: Array<{
        serial: string;
        outcome: { applied: string[]; rejected: { change: string; reason: string }[] };
      }>;
    }>('POST', `/api/admin/v1/sessions/${sessionId}/apply`, {
      changesPerDisk,
      whitelistPerDisk,
      authRegionsPerDisk,
      powerCycleAfter,
    }),

  previewPatch: (
    sessionId: string,
    changesPerDisk: Record<
      string,
      { bom?: string; level?: string; elabelFields?: Record<string, string> }
    >,
  ) =>
    http<{
      sessionId: string;
      disks: Array<{
        serial: string;
        totalBytesChanged: number;
        regions: Array<{ id: number; name: string; bytesChanged: number; firstChangeOffset: number }>;
        outcome: { applied: string[]; rejected: { change: string; reason: string }[] };
      }>;
    }>('POST', `/api/admin/v1/sessions/${sessionId}/preview-patch`, { changesPerDisk }),

  listBackups: (sn: string) =>
    http<
      Array<{
        id: string;
        diskSn: string;
        source: string;
        alsoSources: string[];
        label: string | null;
        parsedSummaryJson: string | null;
        createdAt: number;
      }>
    >('GET', `/api/admin/v1/disk-backups?sn=${encodeURIComponent(sn)}`),

  // Upload a raw .hwrsv archive for a disk SN (octet-stream). The server parses
  // and stores it as a manual backup, flashable via the same restore path.
  // Assumed route: POST /api/admin/v1/disk-backups/upload-hwrsv?sn=<serial>[&label=..]
  uploadBackup: (diskSn: string, bytes: ArrayBuffer, label?: string) => {
    const qs = new URLSearchParams({ sn: diskSn });
    if (label) qs.set('label', label);
    return uploadOctet<{ ok: boolean; backupId: string }>(
      `/api/admin/v1/disk-backups/upload-hwrsv?${qs.toString()}`,
      bytes,
    );
  },

  listAllBackups: (opts: {
    diskSn?: string;
    deviceId?: string;
    source?: string;
    fromTs?: number;
    toTs?: number;
    limit?: number;
    offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (opts.diskSn) qs.set('diskSn', opts.diskSn);
    if (opts.deviceId) qs.set('deviceId', opts.deviceId);
    if (opts.source) qs.set('source', opts.source);
    if (opts.fromTs !== undefined) qs.set('fromTs', String(opts.fromTs));
    if (opts.toTs !== undefined) qs.set('toTs', String(opts.toTs));
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return http<{
      total: number;
      items: Array<{
        id: string;
        diskSn: string;
        deviceId: string | null;
        source: string;
        alsoSources: string[];
        sessionId: string | null;
        parsedSummaryJson: string | null;
        createdAt: number;
        appliedBy: string | null;
      }>;
    }>('GET', `/api/admin/v1/disk-backups/all${suffix}`);
  },

  manualBackup: (sessionId: string, serial: string) =>
    http<{ id: string }>(
      'POST',
      `/api/admin/v1/sessions/${sessionId}/disks/${encodeURIComponent(serial)}/backup`,
    ),

  restore: (sessionId: string, restoreMap: Record<string, string>) =>
    http<{ sessionId: string; approvalId: string; diskCount: number }>(
      'POST',
      `/api/admin/v1/sessions/${sessionId}/restore`,
      { restoreMap },
    ),

  // ---- .hwrsv export / import (admin only) --------------------------------
  // Download one disk's CURRENT state (session inventory) as a .hwrsv archive.
  exportDiskHwrsv: (sessionId: string, serial: string) =>
    downloadBlob(
      `/api/admin/v1/sessions/${sessionId}/export.hwrsv?serials=${encodeURIComponent(serial)}`,
      `${serial}.hwrsv`,
    ),
  // Download a combined .hwrsv of the current inventory state for the given
  // serials (empty → whole session). Pre-apply group backups are a separate
  // route (/backup.hwrsv), linked directly from the post-flash panel.
  exportSelectedHwrsv: (sessionId: string, serials: string[] = []) => {
    const qs = serials.length ? `?serials=${encodeURIComponent(serials.join(','))}` : '';
    return downloadBlob(
      `/api/admin/v1/sessions/${sessionId}/export.hwrsv${qs}`,
      `group-${sessionId.substring(0, 8)}.hwrsv`,
    );
  },
  // Upload a .hwrsv for one disk; the server extracts and returns its elabel
  // fields (the caller loads them as pending edits — no backup is registered).
  importDiskHwrsv: (sessionId: string, serial: string, bytes: ArrayBuffer) =>
    uploadOctet<{ ok?: true; serial?: string; fields?: HwrsvFields }>(
      `/api/admin/v1/sessions/${sessionId}/disks/${encodeURIComponent(serial)}/import-hwrsv`,
      bytes,
    ),
  // Upload a combined .hwrsv; the server returns per-disk extracted fields
  // (matched by serial) for the caller to load as pending edits.
  importHwrsv: (sessionId: string, bytes: ArrayBuffer) =>
    uploadOctet<{
      ok?: true;
      disks?: Array<{ serial: string; fields: HwrsvFields }>;
      skipped?: string[];
    }>(`/api/admin/v1/sessions/${sessionId}/import-hwrsv`, bytes),


  listDevices: () =>
    http<
      Array<{
        id: string;
        fingerprint: string;
        status: string;
        enrolledAt: number | null;
        lastSeen: number | null;
        currentVersion: string | null;
        ownerUserId: string | null;
        ownerUserIds: string[];
        label: string | null;
        online: boolean;
        pending: boolean;
        lastSeenIp: string | null;
        lastError: string | null;
        lastErrorAt: number | null;
        debugUntil: number;
      }>
    >('GET', '/api/admin/v1/devices'),

  listMyDevices: () =>
    http<
      Array<{
        id: string;
        status: string;
        lastSeen: number | null;
        currentVersion: string | null;
        label: string | null;
        ownerUserIds: string[];
        online: boolean;
        lastError: string | null;
        lastErrorAt: number | null;
      }>
    >('GET', '/api/client/v1/devices'),

  revokeDevice: (id: string) => http<{ ok: true }>('POST', `/api/admin/v1/devices/${id}/revoke`),
  reactivateDevice: (id: string) => http<{ ok: true }>('POST', `/api/admin/v1/devices/${id}/reactivate`),
  deleteDevice: (id: string) => http<{ ok: true }>('DELETE', `/api/admin/v1/devices/${id}`),
  approveDevice: (id: string) => http<{ ok: true }>('POST', `/api/admin/v1/devices/${id}/approve`),
  migrateDeviceId: (id: string, newId: string) =>
    http<{ ok: true; mode: 'noop' | 'rename' | 'merge'; movedRows: number }>(
      'POST',
      `/api/admin/v1/devices/${id}/migrate-id`,
      { newId },
    ),
  denyDevice: (id: string) => http<{ ok: true }>('POST', `/api/admin/v1/devices/${id}/deny`),
  renameDevice: (id: string, label: string) =>
    http<{ ok: true; label: string | null }>('POST', `/api/admin/v1/devices/${id}/label`, { label }),
  requestReinventory: (id: string) =>
    http<{ ok: true; online: boolean }>('POST', `/api/admin/v1/devices/${id}/reinventory`),
  powercycleDevice: (deviceId: string, serials: string[]) =>
    http<{ ok: true; serials: string[] }>('POST', `/api/admin/v1/devices/${deviceId}/powercycle`, {
      serials,
    }),
  setDeviceDebug: (id: string, minutes: number) =>
    http<{ ok: true; debugUntil: number }>('POST', `/api/admin/v1/devices/${id}/debug`, { minutes }),
  setDeviceOwners: (deviceId: string, ownerUserIds: string[]) =>
    http<{ ok: true }>('POST', `/api/admin/v1/devices/${deviceId}/owner`, { ownerUserIds }),

  getDeviceLogs: (deviceId: string, opts: { limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return http<{ items: Array<{ id: number; ts: number; message: string }> }>(
      'GET',
      `/api/admin/v1/devices/${deviceId}/logs${suffix}`,
    );
  },

  listFirmwareReleases: () =>
    http<
      Array<{
        id: string;
        version: string;
        binarySize: number;
        mandatory: boolean;
        minProtocolVersion: number;
        publishedAt: number;
        releaseNotes: string | null;
      }>
    >('GET', '/api/admin/v1/firmware/releases'),

  // Edit a release's metadata (superadmin). Changing mandatory/minProtocolVersion
  // re-signs the release server-side. releaseNotes is free-form.
  updateFirmwareRelease: (
    id: string,
    fields: { mandatory?: boolean; minProtocolVersion?: number; releaseNotes?: string },
  ) => http<{ ok: boolean }>('PATCH', `/api/admin/v1/firmware/releases/${id}`, fields),

  // Delete a published release (superadmin).
  deleteFirmwareRelease: (id: string) =>
    http<{ ok: boolean }>('DELETE', `/api/admin/v1/firmware/releases/${id}`),

  // Publish a new firmware release: the server hashes + signs the binary with the
  // Ed25519 update key and stores it; devices then pull/verify/self-apply it.
  publishFirmware: (body: {
    // Optional: the server derives the version from the binary's embedded marker.
    // A value here is only an assertion (must match the embedded one).
    version?: string;
    binaryBase64: string;
    mandatory?: boolean;
    releaseNotes?: string;
    minProtocolVersion?: number;
  }) =>
    http<{
      id: string;
      version: string;
      versionSource: 'embedded' | 'manual';
      size: number;
      sha256: string;
    }>('POST', '/api/admin/v1/firmware/releases', body),

  // Ask a device to self-update to the latest release on its next heartbeat
  // (superadmin). One-shot; the device pulls + verifies + swaps with rollback.
  requestDeviceUpdate: (id: string) =>
    http<{ ok: boolean }>('POST', `/api/admin/v1/devices/${id}/update`),

  listUsers: () =>
    http<
      Array<{
        id: string;
        username: string;
        email?: string | null;
        role: Role;
        createdAt: number;
        note?: string | null;
      }>
    >('GET', '/api/admin/v1/users'),

  createUser: (opts: { username: string; password: string; role: Role; email?: string; note?: string }) =>
    http<{ id: string; username: string; role: Role }>('POST', '/api/admin/v1/users', opts),

  updateUser: (
    id: string,
    opts: { username?: string; email?: string | null; role?: Role; password?: string; note?: string | null },
  ) => http<{ ok: true }>('PATCH', `/api/admin/v1/users/${id}`, opts),

  deleteUser: (id: string) => http<{ ok: true }>('DELETE', `/api/admin/v1/users/${id}`),

  // ----- Support chat -----
  chatMessages: (clientUserId?: string) =>
    http<{ messages: ChatMsg[] }>(
      'GET',
      `/api/chat/messages${clientUserId ? `?clientUserId=${encodeURIComponent(clientUserId)}` : ''}`,
    ),
  chatSend: (body: string, clientUserId?: string) =>
    http<{ message: ChatMsg }>('POST', '/api/chat/send', { body, clientUserId }),
  chatThreads: () => http<{ threads: ChatThread[] }>('GET', '/api/chat/threads'),
  chatUnread: () => http<{ unread: number }>('GET', '/api/chat/unread'),
  chatRead: (clientUserId?: string) => http<{ ok: true }>('POST', '/api/chat/read', { clientUserId }),
  chatClear: (clientUserId?: string) =>
    http<{ ok: true; removed: number }>('POST', '/api/chat/clear', { clientUserId }),

  // Device console relay (superadmin).
  deviceConsoleExec: (deviceId: string, command: string) =>
    http<{ id: number }>('POST', `/api/admin/v1/devices/${deviceId}/console/exec`, { command }),
  deviceConsoleCommands: (deviceId: string) =>
    http<{
      commands: Array<{
        id: number;
        command: string;
        status: string;
        output: string | null;
        exitCode: number | null;
        createdAt: number;
        doneAt: number | null;
      }>;
    }>('GET', `/api/admin/v1/devices/${deviceId}/console/commands`),

  // Superadmin-only destructive powers.
  consoleAvailable: () => http<{ available: boolean }>('GET', '/api/admin/v1/console/available'),
  clearAuditLog: () => http<{ ok: true; removed: number }>('DELETE', '/api/admin/v1/audit'),
  deleteBackup: (id: string) => http<{ ok: true }>('DELETE', `/api/admin/v1/disk-backups/${id}`),

  fetchRegionRaw: (sessionId: string, serial: string, regionId: number) =>
    http<{
      regionId: number;
      regionName: string;
      sizeBytes: number;
      base64: string;
      // Optional wire geometry — present once the server exposes it. When absent
      // HexViewer falls back to the transport passed from the session summary.
      blockSize?: number;
      hasProtectionInfo?: boolean;
      // Read-only structural decode of the region (HwReserveEditor-style).
      parsed?:
        | { kind: 'elabel' | 'innerParam' | 'cert' | 'techParam' | 'fixHeader' | 'authMarker'; fields: Array<{ label: string; value: string }> }
        | { kind: 'regionMap'; signature: string; entries: Array<{ label: string; offset: number; length: number }> }
        | { kind: 'none' };
    }>(
      'GET',
      `/api/admin/v1/sessions/${sessionId}/disks/${encodeURIComponent(serial)}/regions/${regionId}/raw`,
    ),

  myQuota: () =>
    http<{
      userId: string;
      role: string;
      available: number;
      grants: Array<{
        id: string;
        clientUserId: string;
        totalCount: number;
        usedCount: number;
        expiresAt: number | null;
        createdAt: number;
        note: string | null;
      }>;
    }>('GET', '/api/client/v1/quota'),

  listQuotas: (clientUserId?: string) =>
    http<
      Array<{
        id: string;
        clientUserId: string;
        totalCount: number;
        usedCount: number;
        expiresAt: number | null;
        createdAt: number;
        grantedByUserId: string | null;
        grantedByUsername: string | null;
        grantedByEmail: string | null;
        note: string | null;
      }>
    >(
      'GET',
      clientUserId
        ? `/api/admin/v1/quotas?clientUserId=${encodeURIComponent(clientUserId)}`
        : '/api/admin/v1/quotas',
    ),

  grantQuota: (opts: { clientUserId: string; totalCount: number; expiresAt?: number; note?: string }) =>
    http<{ id: string; totalCount: number; available: number }>(
      'POST',
      '/api/admin/v1/quotas',
      opts,
    ),

  // Admin: revoke (deduct) N available units from a client — inverse of grant.
  revokeQuota: (opts: { clientUserId: string; amount: number; note?: string }) =>
    http<{ removed: number; available: number }>(
      'POST',
      '/api/admin/v1/quotas/revoke',
      opts,
    ),

  // ----- Dashboard / statistics -----
  statsSummary: () =>
    http<StatsSummary>('GET', '/api/admin/v1/stats/summary'),

  storageStats: () =>
    http<StorageStats>('GET', '/api/admin/v1/stats/storage'),

  flashStats: (opts: { from?: number; to?: number; bucket?: 'day' | 'week' } = {}) => {
    const qs = new URLSearchParams();
    if (opts.from !== undefined) qs.set('from', String(opts.from));
    if (opts.to !== undefined) qs.set('to', String(opts.to));
    if (opts.bucket) qs.set('bucket', opts.bucket);
    // Bucket on the viewer's midnight, not UTC's — otherwise an 01:00 flash
    // shows up under the previous day's bar for anyone east of Greenwich.
    qs.set('tzOffset', String(-new Date().getTimezoneOffset()));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return http<{
      series: Array<{ ts: number; count: number }>;
      byClient: Array<{
        clientUserId: string;
        clientUsername: string | null;
        count: number;
        uniqueDisks: number;
      }>;
      byDevice: Array<{
        deviceId: string | null;
        deviceLabel: string | null;
        count: number;
        uniqueDisks: number;
      }>;
    }>('GET', `/api/admin/v1/stats/flashes${suffix}`);
  },

  // Soft presence: heartbeat that the caller has device `deviceId` open (omit to
  // just read), returns who else is viewing which device.
  presencePing: (deviceId?: string) =>
    http<{ viewers: Record<string, Array<{ userId: string; username: string }>> }>(
      'POST',
      '/api/admin/v1/presence',
      { deviceId },
    ),
  presenceGet: () =>
    http<{ viewers: Record<string, Array<{ userId: string; username: string }>> }>(
      'GET',
      '/api/admin/v1/presence',
    ),
  // Most recent confirmed flash on a device (for the stale-view warning).
  deviceLastFlash: (deviceId: string) =>
    http<{
      lastFlash: null | { atUnix: number; sessionId: string; actorUserId: string | null; actorUsername: string | null };
    }>('GET', `/api/admin/v1/devices/${deviceId}/last-flash`),

  // Flash history (dashboard drilldown). Server route is clientOrAdmin: a client
  // is scoped to flashes on their own devices; admins see all.
  flashHistory: (opts: { limit?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return http<{
      items: Array<{
        sessionId: string | null;
        whenUnix: number;
        actorId: string | null;
        actorUsername: string | null;
        actorKind: string;
        deviceId: string | null;
        confirmed: boolean;
        disks: Array<{
          serial: string;
          status: string;
          diskType: string | null;
          changes: Array<{ field: string; from: string | null; to: string }>;
        }>;
      }>;
    }>('GET', `/api/admin/v1/stats/flash-history${suffix}`);
  },

  // Admin: any client's quota history (paged).
  quotaLedger: (opts: {
    clientUserId?: string;
    from?: number;
    to?: number;
    limit?: number;
    offset?: number;
  } = {}) => {
    const qs = new URLSearchParams();
    if (opts.clientUserId) qs.set('clientUserId', opts.clientUserId);
    if (opts.from !== undefined) qs.set('from', String(opts.from));
    if (opts.to !== undefined) qs.set('to', String(opts.to));
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return http<{ rows: QuotaLedgerRow[]; total: number }>(
      'GET',
      `/api/admin/v1/quota/ledger${suffix}`,
    );
  },

  // Client (or admin): the CALLER's own quota history only.
  myQuotaLedger: (opts: { limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return http<{ rows: QuotaLedgerRow[]; total: number }>(
      'GET',
      `/api/client/v1/quota/ledger${suffix}`,
    );
  },

  deleteSession: (id: string) =>
    http<{ ok: true }>('DELETE', `/api/admin/v1/sessions/${id}`),

  pruneSessions: (opts: { olderThanDays?: number; status?: string }) =>
    http<{ removed: number }>('POST', '/api/admin/v1/sessions/prune', opts),

  // TOTP
  totpStatus: () => http<{ enabled: boolean }>('GET', '/api/auth/totp/status'),

  // Active login sessions for the current user
  listAuthSessions: () =>
    http<
      Array<{
        id: string;
        idShort: string;
        createdAt: number;
        expiresAt: number;
        ip: string | null;
        userAgent: string | null;
        isCurrent: boolean;
      }>
    >('GET', '/api/auth/sessions'),
  revokeAuthSession: (id: string) => http<{ ok: true }>('POST', `/api/auth/sessions/${id}/revoke`),
  revokeAuthSessionGroup: (ids: string[]) =>
    http<{ ok: true; removed: number }>('POST', '/api/auth/sessions/revoke-group', { ids }),
  revokeOtherAuthSessions: () =>
    http<{ ok: true; removed: number }>('POST', '/api/auth/sessions/revoke-others'),
  totpBegin: () => http<{ secret: string; otpauthUri: string }>('POST', '/api/auth/totp/begin'),
  totpVerify: (code: string) =>
    http<{ ok: true; recoveryCodes: string[] }>('POST', '/api/auth/totp/verify', { code }),

  recoveryCodesCount: () =>
    http<{ remaining: number }>('GET', '/api/auth/recovery-codes/count'),
  recoveryCodesRegenerate: () =>
    http<{ recoveryCodes: string[] }>('POST', '/api/auth/recovery-codes/regenerate'),
  totpDisable: () => http<{ ok: true }>('POST', '/api/auth/totp/disable'),

  // Login with TOTP support (extends existing login)
  loginWithTotp: (username: string, password: string, totp?: string) =>
    http<{ user: User }>('POST', '/api/auth/login', { username, password, totp }),

  listAudit: (opts: { action?: string; actorKind?: string; actorId?: string; q?: string; fromTs?: number; toTs?: number; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (opts.action) qs.set('action', opts.action);
    if (opts.actorKind) qs.set('actorKind', opts.actorKind);
    if (opts.actorId) qs.set('actorId', opts.actorId);
    if (opts.q) qs.set('q', opts.q);
    if (opts.fromTs !== undefined) qs.set('fromTs', String(opts.fromTs));
    if (opts.toTs !== undefined) qs.set('toTs', String(opts.toTs));
    if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
    if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return http<{
      total: number;
      items: Array<{
        id: number;
        actorKind: string;
        actorId: string | null;
        actorEmail: string | null;
        action: string;
        target: string | null;
        detail: unknown;
        ts: number;
        ip: string | null;
        requestId: string | null;
      }>;
    }>('GET', `/api/admin/v1/audit${suffix}`);
  },
};
